import React, { useEffect, useRef, useState } from 'react';
import { DIALOG_TYPE } from '../../../../constants/DialogTypes';

interface IButton {
  label: string;
  onClick?: () => void;
  variant?: 'primary' | 'secondary' | 'link';
  dialogType?: DIALOG_TYPE;
  hasIcon?: boolean;
  icon?: React.ReactNode;
  width?: string;
  disabled?: boolean;
  selected?: boolean;
  type?: 'button' | 'submit';
  hasDropdown?: boolean;
  dropdownItems?: { label: string; onClick: () => void }[];
  className?: string;
}

export const Button: React.FC<IButton> = ({
  label,
  onClick,
  variant = 'primary',
  dialogType,
  hasIcon,
  icon,
  width,
  selected,
  type = 'button',
  disabled = false,
  hasDropdown = false,
  dropdownItems = [],
  className = '',
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const toggleDropdown = () => {
    if (hasDropdown) setIsDropdownOpen((prev) => !prev);
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const baseClass =
    'relative inline-flex min-h-9 items-center justify-center gap-2 px-3 py-1.5 text-[13px] leading-5 font-semibold rounded-lg transition-all duration-200 ease-in-out focus:outline-none active:scale-[0.98] disabled:cursor-not-allowed';

  let buttonClass = `${baseClass} bg-[var(--brand-primary)] text-[var(--brand-text-on-primary)] shadow-lg shadow-[var(--brand-primary)]/20 hover:brightness-95 active:brightness-90 transition-all`;

  if (variant === 'secondary') {
    buttonClass = `${baseClass} bg-white text-slate-700 border border-slate-300 shadow-sm hover:bg-slate-50 hover:shadow-md hover:border-slate-400`;
  } else if (variant === 'link') {
    buttonClass = `${baseClass} bg-transparent text-[var(--brand-primary)] hover:brightness-90 hover:bg-[var(--brand-primary-light)] shadow-none border-none px-2 py-1`;
  }

  const dialogStyles: Record<NonNullable<IButton['dialogType']>, string> = {
    info: `${baseClass} bg-[var(--brand-primary)] text-[var(--brand-text-on-primary)] shadow-lg shadow-[var(--brand-primary)]/25 hover:brightness-95`,
    warning: `${baseClass} bg-gradient-to-r from-amber-500 to-amber-600 text-white shadow-lg shadow-amber-500/25 hover:shadow-xl hover:shadow-amber-500/35 hover:from-amber-600 hover:to-amber-700`,
    success: `${baseClass} bg-gradient-to-r from-green-600 to-green-700 text-white shadow-lg shadow-green-600/25 hover:shadow-xl hover:shadow-green-600/35 hover:from-green-700 hover:to-green-800`,
    error: `${baseClass} bg-gradient-to-r from-red-600 to-red-700 text-white shadow-lg shadow-red-600/25 hover:shadow-xl hover:shadow-red-600/35 hover:from-red-700 hover:to-red-800`,
  };

  const secondaryDialogStyles: Record<
    NonNullable<IButton['dialogType']>,
    string
  > = {
    info: `${baseClass} bg-[var(--brand-primary-light)] text-[var(--brand-primary)] border border-[var(--brand-primary)]/20 hover:bg-[var(--brand-primary-light)]/80`,
    warning: `${baseClass} bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 hover:border-amber-300`,
    success: `${baseClass} bg-green-50 text-green-700 border border-green-200 hover:bg-green-100 hover:border-green-300`,
    error: `${baseClass} bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 hover:border-red-300`,
  };

  if (dialogType) {
    if (variant === 'primary') {
      buttonClass = dialogStyles[dialogType];
    } else if (variant === 'secondary') {
      buttonClass = secondaryDialogStyles[dialogType];
    }
  }

  const getDisabledClass = (buttonVariant: string) => {
    const baseDisabledClass =
      'disabled:opacity-60 disabled:shadow-none disabled:cursor-not-allowed disabled:active:scale-100';

    if (buttonVariant === 'link') {
      return `${baseClass} ${baseDisabledClass} bg-transparent text-gray-400 hover:text-gray-400 hover:bg-transparent shadow-none border-none px-2 py-1`;
    }
    if (buttonVariant === 'secondary') {
      return `${baseClass} ${baseDisabledClass} bg-gray-100 text-gray-500 border border-gray-200 hover:bg-gray-100 hover:border-gray-200 shadow-sm`;
    }
    // For primary buttons and dialogs
    return `${baseClass} ${baseDisabledClass} bg-gray-400 text-white hover:bg-gray-400 shadow-lg`;
  };

  const dynamicPrimaryStyle: React.CSSProperties =
    variant === 'primary' && !dialogType && !disabled
      ? {
          backgroundColor: 'var(--brand-primary)',
          color: 'var(--brand-text-on-primary)',
          width,
        }
      : { width };

  return (
    <div
        className={`relative ${className.includes('w-full') ? 'block w-full sm:inline-block sm:w-auto' : 'inline-block'}`}
        ref={dropdownRef}
    >
      <button
          type={type ?? 'button'}
          onClick={disabled ? undefined : hasDropdown ? toggleDropdown : onClick}
          className={`
            ${disabled ? getDisabledClass(variant) : buttonClass}
            ${className}
          `}
          style={dynamicPrimaryStyle}
          disabled={disabled}
          aria-selected={selected}
      >
        { hasIcon && <span className='flex-shrink-0'>{ icon }</span> }
        <span className='truncate'>{ label }</span>
        { hasDropdown && (
          <svg
              className={`ml-1 w-4 h-4 transform transition-transform duration-200 flex-shrink-0 ${
                isDropdownOpen ? 'rotate-180' : ''
              }`}
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
          >
            <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M19 9l-7 7-7-7'
            />
          </svg>
        ) }
      </button>

      { hasDropdown && isDropdownOpen && (
        <div className='absolute z-50 mt-2 w-full min-w-[200px] bg-white rounded-xl border border-gray-200 shadow-xl shadow-gray-900/10 backdrop-blur-sm animate-fade-in-down overflow-hidden'>
          { dropdownItems.map((item, index) => (
            <button
                key={index}
                onClick={() => {
                  item.onClick();
                  setIsDropdownOpen(false);
                }}
                className='w-full px-4 py-2 text-sm font-medium text-gray-700 text-left hover:bg-gray-50 hover:text-gray-900 active:bg-gray-100 transition-all duration-150 border-b border-gray-100 last:border-b-0'
            >
              { item.label }
            </button>
          )) }
        </div>
      ) }
    </div>
  );
};
